import { Container, Graphics, type Texture } from 'pixi.js';
import { BoardPanel } from './BoardPanel';
import { Confetti } from './Confetti';
import { TitlePlaque } from './TitlePlaque';
import { animate, ease } from './ui';

/**
 * A pop-up over the game: a dimmed screen, a board (the slot's reel floor
 * with a brass edge) and its title on a plaque on the top edge. Draw into
 * {@link content}, at x from 0 to {@link contentWidth}, then call
 * {@link setContentHeight} so the board fits it. Taps on the dim never
 * reach the game underneath.
 */
export class BoardDialog extends Container {
  readonly content = new Container();
  private readonly dim = new Graphics();
  private readonly card = new Container();
  private readonly board: BoardPanel;
  private readonly plaque: TitlePlaque;
  private readonly confetti = new Confetti();
  private viewW = 1;
  private viewH = 1;
  private contentH = 120;
  private insetTop = 0;
  private readonly maxWidth: number;
  /** Padding inside the board: the top leaves room under the plaque. */
  private static readonly PAD = { top: 30, side: 18, bottom: 18 };

  constructor(options: { title: string; floor?: Texture | null; maxWidth?: number; titleSize?: number }) {
    super();
    this.maxWidth = options.maxWidth ?? 360;
    this.board = new BoardPanel({ floor: options.floor ?? null });
    this.plaque = new TitlePlaque(options.title, options.titleSize ?? 17);
    this.dim.eventMode = 'static';
    this.dim.on('pointertap', () => this.onDimTap?.());
    this.card.addChild(this.board, this.content, this.plaque, this.confetti);
    this.addChild(this.dim, this.card);
    this.visible = false;
  }

  /** A tap on the dim around the board (a pop-up the player can dismiss
   *  closes on it). Taps never reach the game underneath either way. */
  onDimTap: (() => void) | null = null;

  setTitle(title: string): void {
    this.plaque.setTitle(title);
  }

  /** The title on the plaque. */
  get titleText(): string {
    return this.plaque.text;
  }

  setFloor(texture: Texture | null): void {
    this.board.setFloor(texture);
  }

  /** The floor art under the board, or null before it has any. */
  get floorTexture(): Texture | null {
    return this.board.floorTexture;
  }

  /** Width the content can use. */
  get contentWidth(): number {
    return this.boardWidth() - BoardDialog.PAD.side * 2;
  }

  /**
   * Room at the top of the screen the board keeps clear (a bar drawn over
   * the canvas, such as the wallet): the board centres in what's left.
   */
  setTopInset(px: number): void {
    if (px === this.insetTop) return;
    this.insetTop = Math.max(0, Math.round(px));
    this.layout(this.viewW, this.viewH);
  }

  /** See {@link setTopInset}. */
  get topInset(): number {
    return this.insetTop;
  }

  setContentHeight(height: number): void {
    this.contentH = Math.max(40, Math.round(height));
    this.layout(this.viewW, this.viewH);
  }

  layout(viewWidth: number, viewHeight: number): void {
    this.viewW = viewWidth;
    this.viewH = viewHeight;
    this.dim.clear().rect(0, 0, viewWidth, viewHeight).fill({ color: 0x0a0602, alpha: 0.72 });
    const w = this.boardWidth();
    const h = this.contentH + BoardDialog.PAD.top + BoardDialog.PAD.bottom;
    this.board.resize(w, h);
    this.content.position.set(BoardDialog.PAD.side, BoardDialog.PAD.top);
    this.plaque.position.set(w / 2, 0);
    this.card.pivot.set(w / 2, h / 2);
    // Centred in the room below the top inset, but never so low that a
    // tall board runs off the bottom.
    const room = viewHeight - this.insetTop;
    const cy = this.insetTop + room / 2;
    const lowest = viewHeight - h / 2 - 8;
    this.card.position.set(Math.round(viewWidth / 2), Math.round(Math.max(Math.min(cy, lowest), h / 2 + 8)));
  }

  /**
   * A small win: confetti bursts from `at` (content space; the middle of
   * the board by default). Every game calls this when the player wins
   * (producer 2026-10-14).
   */
  celebrate(at?: { x: number; y: number }): Promise<void> {
    const w = this.boardWidth();
    const x = at ? at.x + BoardDialog.PAD.side : w / 2;
    const y = at ? at.y + BoardDialog.PAD.top : (this.contentH + BoardDialog.PAD.top) / 2;
    return this.confetti.burst(x, y, { count: 90, spread: Math.max(260, w) });
  }

  async open(): Promise<void> {
    this.confetti.clear();
    this.visible = true;
    this.alpha = 0;
    await animate(220, (t) => {
      this.alpha = t;
      this.card.scale.set(0.92 + 0.08 * ease.outBack(t));
    });
  }

  async close(): Promise<void> {
    await animate(160, (t) => {
      this.alpha = 1 - t;
      this.card.scale.set(1 - 0.04 * t);
    });
    // Its scene may have gone mid-close.
    if (!this.destroyed) this.visible = false;
  }

  private boardWidth(): number {
    return Math.min(this.maxWidth, Math.max(240, this.viewW - 32));
  }
}
